import { useEffect } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createHeadingNode, $isHeadingNode } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import {
  $getSelection,
  $isRangeSelection,
  createCommand,
  COMMAND_PRIORITY_EDITOR,
  LexicalCommand,
  LexicalNode,
  $createParagraphNode,
  BaseSelection
} from 'lexical'
import { $findMatchingParent } from '@lexical/utils'

// Define the command type
type HeadingPayload = 1 | 2 | 3 | 4 | 5 | 6

export const INSERT_HEADING_COMMAND: LexicalCommand<HeadingPayload> =
  createCommand()
export const REMOVE_HEADING_COMMAND: LexicalCommand<HeadingPayload> =
  createCommand()

export function hasHeadingInSelection(
  selection: BaseSelection | null,
  level: number
): boolean {
  if (!selection) return false

  const nodes = Array.from(selection.getNodes())

  const isHeadingNode = (node: LexicalNode) =>
    $isHeadingNode(node) && node.getTag() === `h${level}`

  return nodes.some((node: LexicalNode) => {
    if (isHeadingNode(node)) return true
    return !!$findMatchingParent(node, node => isHeadingNode(node))
  })
}

// Create the plugin to register the command
export function HeadingPlugin(): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    const insertHeading = editor.registerCommand<HeadingPayload>(
      INSERT_HEADING_COMMAND,
      payload => {
        editor.update(() => {
          const selection = $getSelection()

          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createHeadingNode(`h${payload}`))
          }
        })

        return true
      },
      COMMAND_PRIORITY_EDITOR
    )

    const removeHeading = editor.registerCommand(
      REMOVE_HEADING_COMMAND,
      () => {
        editor.update(() => {
          const selection = $getSelection()

          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createParagraphNode())
          }
        })
        return true
      },
      COMMAND_PRIORITY_EDITOR
    )

    return () => {
      removeHeading()
      insertHeading()
    }
  }, [editor])

  return null
}
